import { LoaderCircle } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { Alert, AlertDescription } from '../../../../components/ui/alert.js';
import { Button } from '../../../../components/ui/button.js';
import { Combobox } from '../../../../components/ui/combobox.js';
import { Input } from '../../../../components/ui/input.js';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../../components/ui/select.js';
import { cloneCustomForm } from '../actions.js';
import {
  useActionLog,
  useCustomForms,
  useCustomFormDetails,
  useCustomFormsApi,
  useFilteredWorkspaceOptions,
  resolveActionError,
} from '../hooks.js';
import type { WorkfrontWorkspaceOption } from '../types.js';

interface CloneCustomFormProps {
  workspaceOptions: ReadonlyArray<WorkfrontWorkspaceOption>;
  workspaceError: string | null;
  onCancel: () => void;
}

/**
 * Clone-Custom-Form wizard. Port of
 * `wf-audit/src/slices/workfront/components/custom-forms-migration/
 * clone-custom-form/CloneCustomForm.tsx` adapted for the new SPA's
 * UI primitives (shadcn `Select` + `Combobox` + `Input`).
 *
 * Flow:
 *  1. Pick a source workspace (today: only `primary` is supported by
 *     the engine; secondary returns 501 — the dropdown surfaces the
 *     hint but the operator can still attempt and see the upstream
 *     error)
 *  2. Pick a source form (TanStack-cached, filterable combobox)
 *  3. Pick a destination workspace
 *  4. Type a new form name (defaults to the source name)
 *  5. Click Clone — validates name uniqueness, matches groups/params
 *     on the target, creates missing groups + params, then creates
 *     the form. Streams progress lines into a console-style log.
 */
export function CloneCustomForm({
  workspaceOptions,
  workspaceError,
  onCancel,
}: CloneCustomFormProps): JSX.Element {
  const api = useCustomFormsApi();
  const [sourceWorkspaceRef, setSourceWorkspaceRef] = useState('');
  const [sourceFormId, setSourceFormId] = useState<string | null>(null);
  const [targetWorkspaceRef, setTargetWorkspaceRef] = useState('');
  const [newName, setNewName] = useState('');

  const [isCloning, setIsCloning] = useState(false);
  const [cloneError, setCloneError] = useState('');
  const [cloneSuccess, setCloneSuccess] = useState(false);
  const [clonedFormUrl, setClonedFormUrl] = useState('');

  const bottomRef = useRef<HTMLDivElement>(null);
  const { logs, addLog, clearLogs } = useActionLog();

  const sourceForms = useCustomForms(sourceWorkspaceRef);
  const formDetails = useCustomFormDetails({
    workspaceRef: sourceWorkspaceRef,
    formId: sourceFormId,
  });

  // The legacy SPA filters source ≠ target. We mirror that — but
  // when only one workspace is configured the target dropdown will
  // be empty; the operator sees the workspace-error hint above.
  const sourceOptions = useFilteredWorkspaceOptions(workspaceOptions, targetWorkspaceRef);
  const targetOptions = useFilteredWorkspaceOptions(workspaceOptions, sourceWorkspaceRef);

  useEffect(() => {
    if (formDetails.formDetails?.name) {
      setNewName(formDetails.formDetails.name);
    }
  }, [formDetails.formDetails?.name]);

  // Reset the form selection when the source workspace changes.
  useEffect(() => {
    setSourceFormId(null);
  }, [sourceWorkspaceRef]);

  const isDisabled = formDetails.isLoading || sourceForms.isLoading || isCloning;
  const canClone =
    !!formDetails.formDetails && !!targetWorkspaceRef && !!newName.trim() && !isDisabled;

  const handleClone = async () => {
    if (!formDetails.formDetails || !targetWorkspaceRef || !newName.trim()) return;

    setIsCloning(true);
    setCloneError('');
    setCloneSuccess(false);
    setClonedFormUrl('');
    clearLogs();

    try {
      const { errors, formId } = await cloneCustomForm(
        api,
        formDetails.formDetails,
        { workspaceRef: targetWorkspaceRef },
        newName.trim(),
        addLog,
      );
      if (errors.length) {
        setCloneError(errors.join('; '));
      } else {
        setCloneSuccess(true);
        if (formId) {
          const targetWs = workspaceOptions.find((o) => o.value === targetWorkspaceRef);
          if (targetWs && targetWs.text.startsWith('http')) {
            setClonedFormUrl(`${targetWs.text}/form-builder/${formId}`);
          }
        }
      }
    } catch (error) {
      const message = resolveActionError(error, 'Clone failed');
      setCloneError(message);
      addLog(`ERROR: ${message}`);
    } finally {
      setIsCloning(false);
      setTimeout(() => bottomRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
    }
  };

  return (
    <div className="mx-auto flex max-w-2xl flex-col gap-5 px-6 py-6 text-left">
      <h2 className="text-2xl font-light text-foreground">Clone Custom Form</h2>

      <Alert className="border-amber-400/30 bg-amber-500/10 text-amber-100">
        <AlertDescription>
          <b>Note:</b> Conditional logic rules (show/hide, required-if) cannot be migrated
          automatically and must be recreated manually in the target environment. The Workfront API
          does not expose logic rule definitions.
        </AlertDescription>
      </Alert>

      {workspaceError && (
        <Alert className="border-rose-400/30 bg-rose-500/10 text-rose-100">
          <AlertDescription>{workspaceError}</AlertDescription>
        </Alert>
      )}

      <div className="flex flex-col gap-1">
        <label htmlFor="src-ws" className="text-sm font-semibold text-foreground/85">
          Source Instance
        </label>
        <Select
          value={sourceWorkspaceRef}
          onValueChange={setSourceWorkspaceRef}
          disabled={isDisabled}
        >
          <SelectTrigger
            id="src-ws"
            className="w-full border-foreground/15 bg-foreground/5 text-foreground"
          >
            <SelectValue placeholder="Select source workspace…" />
          </SelectTrigger>
          <SelectContent>
            {sourceOptions.map((opt) => (
              <SelectItem key={opt.value} value={opt.value}>
                {opt.text}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="flex flex-col gap-1">
        <span className="text-sm font-semibold text-foreground/85">Source Custom Form</span>
        <Combobox
          value={sourceFormId ?? undefined}
          onValueChange={(value) => setSourceFormId(value || null)}
          disabled={!sourceWorkspaceRef || sourceForms.isLoading || isDisabled}
          placeholder={sourceForms.isLoading ? 'Loading custom forms…' : 'Custom Form'}
          emptyText={sourceForms.isLoading ? 'Loading custom forms…' : 'No custom forms found'}
          searchPlaceholder="Search custom forms…"
          options={sourceForms.forms.map((form) => ({ value: form.ID, label: form.name }))}
        />
        {sourceForms.error && (
          <div className="mt-1 text-sm text-rose-800 dark:text-rose-200">
            Failed to load custom forms: {sourceForms.error.message}
          </div>
        )}
      </div>

      {formDetails.formDetails &&
        (() => {
          const paramsWithExpressions = formDetails.formDetails.groups.flatMap((group) =>
            group.params.filter((p) => p.categoryParameterExpression?.customExpression),
          );
          if (!paramsWithExpressions.length) return null;
          return (
            <Alert className="border-amber-400/30 bg-amber-500/10 text-amber-100">
              <AlertDescription>
                <b>Note:</b> The following parameters have calculated expressions that cannot be
                migrated automatically and must be recreated manually in the target environment:
                <ul className="mt-1 list-inside list-disc">
                  {paramsWithExpressions.map((p) => (
                    <li key={p.id}>
                      <span className="font-medium">{p.name}</span>
                      <span className="ml-1 text-xs opacity-75">
                        ({p.categoryParameterExpression?.customExpression})
                      </span>
                    </li>
                  ))}
                </ul>
              </AlertDescription>
            </Alert>
          );
        })()}

      <hr className="border-foreground/10" />

      <div className="flex flex-col gap-1">
        <label htmlFor="tgt-ws" className="text-sm font-semibold text-foreground/85">
          Target Instance
        </label>
        <Select
          value={targetWorkspaceRef}
          onValueChange={setTargetWorkspaceRef}
          disabled={isDisabled}
        >
          <SelectTrigger
            id="tgt-ws"
            className="w-full border-foreground/15 bg-foreground/5 text-foreground"
          >
            <SelectValue
              placeholder={
                targetOptions.length === 0
                  ? 'No other workspaces configured'
                  : 'Select target workspace…'
              }
            />
          </SelectTrigger>
          <SelectContent>
            {targetOptions.map((opt) => (
              <SelectItem key={opt.value} value={opt.value}>
                {opt.text}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {targetOptions.length === 0 && sourceWorkspaceRef && (
          <p className="mt-1 text-xs text-foreground/55">
            Clone requires a second workspace. Configure a secondary Workfront workspace on this
            license to enable the target picker.
          </p>
        )}
      </div>

      <div className="flex flex-col gap-1">
        <label htmlFor="clone-new-name" className="text-sm font-semibold text-foreground/85">
          New Name
        </label>
        <Input
          id="clone-new-name"
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
          placeholder="New Deliverable Metadata"
          disabled={isDisabled}
          className="border-foreground/15 bg-foreground/5 text-foreground placeholder:text-foreground/40"
        />
      </div>

      {cloneError && (
        <Alert className="border-rose-400/30 bg-rose-500/10 text-rose-100">
          <AlertDescription>{cloneError}</AlertDescription>
        </Alert>
      )}

      {cloneSuccess && (
        <Alert className="border-emerald-400/30 bg-emerald-500/10 text-emerald-100">
          <AlertDescription>
            Custom form cloned successfully.
            {clonedFormUrl && (
              <>
                {' '}
                <a
                  href={clonedFormUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline hover:no-underline"
                >
                  Open in Workfront
                </a>
              </>
            )}
          </AlertDescription>
        </Alert>
      )}

      {logs.length > 0 && (
        <div className="max-h-[200px] overflow-auto rounded-md border border-foreground/10 bg-black/30 p-3 font-mono text-xs text-foreground/75">
          {logs.map((log, index) => (
            <div key={index} className="border-b border-foreground/5 py-0.5 last:border-b-0">
              {log}
            </div>
          ))}
        </div>
      )}

      <div ref={bottomRef} />

      <div className="sticky bottom-0 flex gap-3 border-t border-foreground/10 bg-black/40 py-4 backdrop-blur">
        {cloneSuccess ? (
          <Button variant="outline" onClick={onCancel}>
            Close
          </Button>
        ) : (
          <>
            <Button disabled={!canClone} onClick={() => void handleClone()}>
              {isCloning ? (
                <>
                  <LoaderCircle className="mr-2 h-4 w-4 animate-spin" />
                  Cloning…
                </>
              ) : (
                'Clone'
              )}
            </Button>
            <Button variant="outline" onClick={onCancel} disabled={isCloning}>
              Cancel
            </Button>
          </>
        )}
      </div>
    </div>
  );
}
